import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import {
  type RigaPrezzo,
  esportaListino,
  useDuplicaListino,
  useListino,
  useOpzioniListini,
  useSalvaRigaListino,
  useTogliDestinazione,
  useTogliRigaListino,
} from '../api/listini.js';
import { Dato } from '../componenti/Dato.js';
import { Modale } from '../componenti/Modale.js';
import { type Colonna, Tabella } from '../componenti/Tabella.js';
import { giorno, importo } from '../formati.js';
import { VALIDITA } from './Listini.js';
import { ModaleListino } from './ModaleListino.js';
import { ModaleRigaListino } from './ModaleRigaListino.js';
import { t } from '../i18n/lingua.js';

/**
 * La scheda di un listino.
 *
 * Due cose contano: **duplicare**, perché è così che nasce un listino nuovo —
 * quello Campbell ha centoquindici righe e ogni trimestre se ne fa uno
 * cambiando qualche prezzo — e **correggere un prezzo**, che si fa sulla riga
 * senza aprire nessuna finestra.
 */

export function Listino({ id }: { id: number }) {
  const [copiaAperta, setCopiaAperta] = useState(false);
  const [testataAperta, setTestataAperta] = useState(false);
  /**
   * La riga aperta, per identificativo e non come copia: la finestra deve
   * vedere i prezzi per destinazione appena salvati, e una copia presa al
   * clic resterebbe quella di prima finché non la si chiude.
   */
  const [rigaAperta, setRigaAperta] = useState<number | null | undefined>(undefined);
  /**
   * «Togli» chiede conferma sul posto, come gli allegati: una riga tolta per
   * sbaglio porta via prezzo, sconti e prezzi per destinazione. La × di un
   * prezzo per destinazione invece toglie subito, deciso con Antonio il 15
   * ottobre 2026: è un prezzo solo, e si rimette in un attimo.
   */
  const [daTogliere, setDaTogliere] = useState<number | null>(null);
  const dati = useListino(id);
  const opzioni = useOpzioniListini();
  const salvaRiga = useSalvaRigaListino(id);
  const togliRiga = useTogliRigaListino(id);
  const togliDestinazione = useTogliDestinazione(id);
  /**
   * Il prezzo si corregge cliccandolo, la riga e il prezzo per destinazione
   * si tolgono con un clic: se il servizio dice di no, va detto. Prima non
   * compariva niente, e il prezzo sembrava salvato.
   */
  const erroreSuiPrezzi = [salvaRiga.error, togliRiga.error, togliDestinazione.error]
    .map((e) =>
      e instanceof ErroreApi ? e.message : e ? t('La modifica non è stata salvata: riprova.') : null,
    )
    .find(Boolean);

  // con un numero non valido la lettura non parte nemmeno: «in attesa» per
  // sempre. Si aspetta solo una lettura vera, e il resto è «non disponibile»
  if (dati.isLoading) return <div className="cantiere">{t('Un momento…')}</div>;

  if (dati.error || !dati.data) {
    const messaggio =
      dati.error instanceof ErroreApi
        ? dati.error.message
        : t('Non è stato possibile aprire questo listino.');
    return (
      <>
        <div className="page-head">
          <div>
            <Link className="back-link" to="/price-lists">
              {t('← Torna ai listini')}
            </Link>
            <h1 className="page-title">{t('Listino non disponibile')}</h1>
          </div>
        </div>
        <div className="cantiere">
          <p>{messaggio}</p>
        </div>
      </>
    );
  }

  const l = dati.data!;
  const validita = VALIDITA[l.validity];
  const conDestinazioni = l.lines.filter((r) => r.destinations.length > 0).length;
  const rigaInModifica = l.lines.find((r) => r.id === rigaAperta) ?? null;

  const colonne: Colonna<RigaPrezzo>[] = [
    {
      chiave: 'description',
      titolo: t('Articolo'),
      larghezzaMax: 300,
      cella: (r) => (
        <>
          <Link className="cell-link" to="/items/$id" params={{ id: String(r.articleId) }}>
            {r.description || r.sku}
          </Link>
          {r.crossReference && (
            <span className="dim" style={{ marginLeft: 6, fontSize: 11 }}>
              {t('rif. {codice}', { codice: r.crossReference })}
            </span>
          )}
        </>
      ),
    },
    { chiave: 'sku', titolo: 'SKU', monospazio: true, larghezzaMax: 240 },
    {
      chiave: 'priceExw',
      titolo: t('Prezzo'),
      allineamento: 'destra',
      // il prezzo si corregge qui: è la modifica che si fa, e passare da una
      // finestra per cambiare un numero sarebbe un clic di troppo per riga
      cella: (r) => (
        <PrezzoModificabile
          valore={r.priceExw}
          inCorso={salvaRiga.isPending}
          onSalva={(priceExw) => salvaRiga.mutate({ lineId: r.id, priceExw })}
        />
      ),
    },
    {
      chiave: 'costDdp',
      titolo: t('Costo DDP'),
      allineamento: 'destra',
      monospazio: true,
      cella: (r) =>
        Number(r.costDdp) !== 0 ? importo(r.costDdp) : <span className="dim">—</span>,
    },
    {
      chiave: 'discountPercentage1',
      titolo: t('Sconti'),
      allineamento: 'destra',
      monospazio: true,
      cella: (r) => {
        const sconti = [r.discountPercentage1, r.discountPercentage2, r.discountPercentage3]
          .filter((s) => s !== null && Number(s) !== 0)
          .map((s) => `${Number(s).toLocaleString('it-IT')}%`);
        return sconti.length ? sconti.join(' + ') : <span className="dim">—</span>;
      },
    },
    { chiave: 'tax', titolo: t('Aliquota') },
    {
      chiave: 'destinations',
      titolo: t('Per destinazione'),
      cella: (r) =>
        r.destinations.length === 0 ? (
          <span className="dim">—</span>
        ) : (
          <span className="dest-elenco">
            {r.destinations.map((d) => (
              <span
                key={d.id}
                className="dest"
                title={[d.address, d.city, d.country].filter(Boolean).join(', ')}
              >
                {/* si accorcia il nome, mai il prezzo: è quello che si cerca */}
                <span className="dest-nome">{d.address ?? t('Destinazione {n}', { n: d.addressId })}</span>
                <b>{importo(d.priceExw)}</b>
                <button
                  className="chip-x"
                  aria-label={t('Togli il prezzo per {dove}', { dove: d.address ?? t('questa destinazione') })}
                  onClick={() => togliDestinazione.mutate({ lineId: r.id, destId: d.id })}
                >
                  ×
                </button>
              </span>
            ))}
          </span>
        ),
    },
    {
      chiave: '_azioni',
      titolo: '',
      allineamento: 'destra',
      cella: (r) => (
        <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={() => setRigaAperta(r.id)}>
            {t('Modifica')}
          </button>
          {daTogliere === r.id ? (
            <>
              <button className="btn btn-ghost" onClick={() => setDaTogliere(null)}>
                {t('Lascia stare')}
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  togliRiga.mutate(r.id);
                  setDaTogliere(null);
                }}
              >
                {t('Togli davvero')}
              </button>
            </>
          ) : (
            <button
              className="btn btn-ghost"
              onClick={() => setDaTogliere(r.id)}
              title={t('Toglie l\'articolo dal listino')}
            >
              {t('Togli')}
            </button>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <Link className="back-link" to="/price-lists">
            {t('← Torna ai listini')}
          </Link>
          <h1 className="page-title">{l.name}</h1>
          <div className="page-sub">
            {l.customerId ? (
              <Link className="cell-link" to="/customers/$id" params={{ id: String(l.customerId) }}>
                {l.customer}
              </Link>
            ) : (
              (l.country ?? 'senza cliente')
            )}
            {' · '}
            {giorno(l.startDate)} → {giorno(l.endDate)}
            {' · '}
            {l.shippingMethod.toUpperCase()}
            {l.archived && ` · ${t('archiviato')}`}
          </div>
        </div>
        <div className="page-actions">
          <span className={`badge ${validita.classe}`}>{validita.testo}</span>
          <span className="btn-sep" />
          <button
            className="btn"
            onClick={() => esportaListino(l.id)}
            title={t('Scarica il listino in Excel, con i prezzi per destinazione')}
          >
            Excel
          </button>
          <button className="btn" onClick={() => setCopiaAperta(true)}>
            {t('Duplica')}
          </button>
          <button className="btn btn-primary" onClick={() => setTestataAperta(true)}>
            {t('Modifica')}
          </button>
        </div>
      </div>

      <div className="grid grid-2">
        <div className="card span-2">
          <div className="card-head">
            <span className="card-title">{t('Listino')}</span>
          </div>
          <div className="card-body">
            <div className="fields">
              <Dato etichetta="Valido dal">{giorno(l.startDate)}</Dato>
              <Dato etichetta="Valido fino al">{giorno(l.endDate)}</Dato>
              <Dato etichetta="Resa">{l.shippingMethod.toUpperCase()}</Dato>
              <Dato etichetta="Sconto globale">
                {l.flatDiscountPercentage && Number(l.flatDiscountPercentage) !== 0
                  ? `${Number(l.flatDiscountPercentage).toLocaleString('it-IT')}%`
                  : null}
              </Dato>
              <Dato etichetta="Articoli a listino" mono>
                {l.lines.length.toLocaleString('it-IT')}
              </Dato>
              <Dato
                etichetta="Con prezzi per destinazione"
                mono
                nota={conDestinazioni > 0 ? t(' — battono la riga') : undefined}
              >
                {conDestinazioni > 0 ? conDestinazioni.toLocaleString('it-IT') : null}
              </Dato>
              {l.notes && (
                <Dato etichetta="Annotazioni" pieno>
                  <span style={{ whiteSpace: 'pre-wrap' }}>{l.notes}</span>
                </Dato>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ margin: '14px 20px 24px' }}>
        <div className="card-head">
          <span className="card-title">{t('Prezzi')}</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 12 }}>
            {t('il prezzo si corregge cliccandolo')}
          </span>
          <button className="btn btn-primary" onClick={() => setRigaAperta(null)}>
            {t('Aggiungi articolo')}
          </button>
        </div>
        {erroreSuiPrezzi && (
          <div className="avviso" role="alert" style={{ margin: '12px 12px 0' }}>
            <span className="avviso-segno">▲</span>
            <span>{erroreSuiPrezzi}</span>
          </div>
        )}
        <Tabella
          colonne={colonne}
          righe={l.lines}
          chiaveRiga={(r) => r.id}
          vuoto={t('Questo listino non ha ancora articoli: aggiungine uno, o duplica un listino che li ha già.')}
        />
      </div>

      {copiaAperta && <ModaleCopia listino={l} onChiudi={() => setCopiaAperta(false)} />}

      {testataAperta && (
        <ModaleListino
          listino={l}
          opzioni={opzioni.data}
          onChiudi={() => setTestataAperta(false)}
        />
      )}

      {(rigaAperta === null || rigaInModifica) && (
        <ModaleRigaListino
          listinoId={id}
          riga={rigaInModifica}
          opzioni={opzioni.data}
          giaPresenti={l.lines.map((r) => r.articleId)}
          onChiudi={() => setRigaAperta(undefined)}
        />
      )}
    </>
  );
}

/**
 * Un prezzo che si modifica dove sta.
 *
 * Si clicca, si scrive, si conferma con Invio; Esc annulla. Non c'è
 * salvataggio automatico mentre si digita: un prezzo scritto a metà — «3» di
 * «38,50» — non deve arrivare a database nemmeno per un istante.
 */
function PrezzoModificabile({
  valore,
  inCorso,
  onSalva,
}: {
  valore: string;
  inCorso: boolean;
  onSalva: (nuovo: number) => void;
}) {
  const [aperto, setAperto] = useState(false);

  if (!aperto) {
    return (
      <button className="prezzo" onClick={() => setAperto(true)} title={t('Cambia il prezzo')}>
        {importo(valore)}
      </button>
    );
  }

  const conferma = (testo: string) => {
    const nuovo = Number(testo.replace(',', '.'));
    setAperto(false);
    if (Number.isFinite(nuovo) && nuovo >= 0 && nuovo !== Number(valore)) onSalva(nuovo);
  };

  return (
    <input
      className="input num"
      style={{ height: 24, width: 96, textAlign: 'right' }}
      defaultValue={valore}
      autoFocus
      disabled={inCorso}
      onKeyDown={(e) => {
        if (e.key === 'Enter') conferma((e.target as HTMLInputElement).value);
        if (e.key === 'Escape') setAperto(false);
      }}
      onBlur={(e) => conferma(e.target.value)}
    />
  );
}

/**
 * La copia.
 *
 * Il nome e le date si propongono già compilati: il caso normale è «lo stesso
 * listino per il trimestre dopo», e riscrivere il nome ogni volta è lavoro
 * che il programma può fare.
 */
function ModaleCopia({
  listino,
  onChiudi,
}: {
  listino: { id: number; name: string; startDate: string | null; endDate: string | null };
  onChiudi: () => void;
}) {
  const navigate = useNavigate();
  const duplica = useDuplicaListino(listino.id);

  /**
   * Le date si contano in giorni locali, non in UTC.
   *
   * A database sono `timestamptz` a mezzanotte italiana, cioè le 22 o le 23
   * del giorno prima in UTC: passando per `toISOString` il listino che finisce
   * il 31 ottobre farebbe cominciare la copia il 31 ottobre — un giorno in cui
   * varrebbero due listini.
   */
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const trimestreDopo = () => {
    const fine = listino.endDate ? new Date(listino.endDate) : new Date();
    const inizio = new Date(fine.getFullYear(), fine.getMonth(), fine.getDate() + 1);
    const nuovaFine = new Date(inizio.getFullYear(), inizio.getMonth() + 3, inizio.getDate() - 1);
    return { inizio, fine: nuovaFine };
  };
  const proposta = trimestreDopo();

  const [nome, setNome] = useState(
    `${listino.name.replace(/\s*\d{2}\/\d{2}\/\d{4}.*$/, '').trim()} ${iso(proposta.inizio)
      .split('-')
      .reverse()
      .join('/')} al ${iso(proposta.fine).split('-').reverse().join('/')}`,
  );
  const [dal, setDal] = useState(iso(proposta.inizio));
  const [al, setAl] = useState(iso(proposta.fine));

  const errore = duplica.error instanceof ErroreApi ? duplica.error.message : null;

  const conferma = () =>
    duplica.mutate(
      { name: nome.trim(), startDate: dal || null, endDate: al || null },
      {
        onSuccess: (copia) => {
          onChiudi();
          void navigate({ to: '/price-lists/$id', params: { id: String(copia.id) } });
        },
      },
    );

  return (
    <Modale
      titolo={t('Duplica il listino')}
      nota={t('Si copiano tutte le righe, i prezzi per destinazione, gli sconti e le aliquote. I prezzi restano quelli di partenza: è da lì che si corregge.')}
      inCorso={duplica.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={duplica.isPending}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            onClick={conferma}
            disabled={duplica.isPending || nome.trim().length === 0}
          >
            {duplica.isPending ? 'Copio…' : 'Duplica'}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}
      <div className="fields">
        <div className="field span-2">
          <label htmlFor="copia-nome">
            {t('Nome del nuovo listino')}<span className="req">*</span>
          </label>
          <input
            id="copia-nome"
            className="input"
            value={nome}
            autoFocus
            onChange={(e) => setNome(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="copia-dal">{t('Valido dal')}</label>
          <input
            id="copia-dal"
            className="input"
            type="date"
            value={dal}
            onChange={(e) => setDal(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="copia-al">{t('Valido fino al')}</label>
          <input
            id="copia-al"
            className="input"
            type="date"
            value={al}
            onChange={(e) => setAl(e.target.value)}
          />
        </div>
      </div>
    </Modale>
  );
}
